/** Debounced search hook with AbortController for request cancellation.
 *  Includes a session-scoped result cache (memory + sessionStorage) and
 *  background prefetch of the opposite site tab so tab switching is instant. */

import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { search as apiSearch, autocomplete as apiAutocomplete } from '../api';
import type { SearchResponse, AutocompleteResponse, SearchParams } from '../api';
import { getConfig } from '../config';

// Bump suffix when ranking semantics change so existing user sessions
// invalidate stale responses from before the deploy (sessionStorage isn't
// cleared by "Clear browser cache" — it persists for the tab's lifetime).
// v2: 2026-04-27 — branded-coverage boost + dead-link filter.
// v6: 2026-05-11 — stable rollback cache line after ranking and widget
//                  layout fixes; older tabs can retain stale sessionStorage.
const SESSION_CACHE_KEY = 'terraSearchCache_v6';
const SESSION_CACHE_MAX = 30;

function cacheKey(p: SearchParams): string {
  return JSON.stringify([
    p.q, p.page ?? 1, p.per_page ?? 40, p.sort ?? 'relevance',
    p.vendor ?? '', p.site ?? '', p.manufacturer ?? '',
    p.category ?? '', p.certifications ?? '',
    p.in_stock ? 1 : 0, p.price_min ?? 0, p.price_max ?? 0,
    p.semantic ? 1 : 0, p.dedup_families ? 1 : 0,
  ]);
}

function loadCache(): Map<string, SearchResponse> {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return new Map();
    const entries = JSON.parse(raw) as Array<[string, SearchResponse]>;
    return new Map(entries);
  } catch {
    return new Map();
  }
}

function saveCache(cache: Map<string, SearchResponse>) {
  try {
    const entries = Array.from(cache.entries()).slice(-SESSION_CACHE_MAX);
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(entries));
  } catch {
    /* quota exceeded or storage disabled — silent no-op */
  }
}

export interface UseSearchReturn {
  results: SearchResponse | null;
  autocompleteResults: AutocompleteResponse | null;
  instantResults: SearchResponse | null;
  isLoading: boolean;
  isAutocompleteLoading: boolean;
  isInstantSearchLoading: boolean;
  isInstantLoadingMore: boolean;
  error: string | null;
  doSearch: (params: SearchParams) => void;
  doAutocomplete: (query: string) => void;
  doInstantSearch: (query: string) => void;
  loadMoreInstant: () => void;
  clearAutocomplete: () => void;
  clearInstantResults: () => void;
}

export function useSearch(): UseSearchReturn {
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [autocompleteResults, setAutocompleteResults] = useState<AutocompleteResponse | null>(null);
  const [instantResults, setInstantResults] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [isInstantSearchLoading, setIsInstantSearchLoading] = useState(false);
  const [isInstantLoadingMore, setIsInstantLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const instantPageRef = useRef(1);
  const instantQueryRef = useRef('');

  const searchAbortRef = useRef<AbortController | null>(null);
  const acAbortRef = useRef<AbortController | null>(null);
  const acTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const instantAbortRef = useRef<AbortController | null>(null);
  const instantTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef<Map<string, SearchResponse>>(loadCache());
  const prefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prefetchOppositeSite = useCallback((params: SearchParams) => {
    if (!params.site) return;
    const otherSite = params.site === 'le' ? 'terra' : 'le';
    const otherParams: SearchParams = { ...params, site: otherSite };
    const otherKey = cacheKey(otherParams);
    if (cacheRef.current.has(otherKey)) return;
    apiSearch(otherParams)
      .then((data) => {
        cacheRef.current.set(otherKey, data);
        saveCache(cacheRef.current);
      })
      .catch(() => { /* background prefetch — silent failure */ });
  }, []);

  const doSearch = useCallback((params: SearchParams) => {
    const key = cacheKey(params);
    const cached = cacheRef.current.get(key);
    if (cached) {
      searchAbortRef.current?.abort();
      setResults(cached);
      setIsLoading(false);
      setError(null);
      pushGA4ViewItemList(params.q, cached);
      // Still kick off opposite-site prefetch (cheap if already cached)
      if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = setTimeout(() => prefetchOppositeSite(params), 200);
      return;
    }

    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setIsLoading(true);
    setError(null);

    apiSearch(params, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          cacheRef.current.set(key, data);
          saveCache(cacheRef.current);
          setResults(data);
          setIsLoading(false);
          pushGA4ViewItemList(params.q, data);
          if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
          prefetchTimerRef.current = setTimeout(() => prefetchOppositeSite(params), 300);
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Search failed');
          setIsLoading(false);
        }
      });
  }, [prefetchOppositeSite]);

  const doAutocomplete = useCallback((query: string) => {
    const { debounceMs } = getConfig();

    // Clear previous timer
    if (acTimerRef.current) clearTimeout(acTimerRef.current);

    if (!query || query.length < 2) {
      setAutocompleteResults(null);
      return;
    }

    acTimerRef.current = setTimeout(() => {
      acAbortRef.current?.abort();
      const controller = new AbortController();
      acAbortRef.current = controller;

      setIsAutocompleteLoading(true);

      apiAutocomplete(query, undefined, controller.signal)
        .then((data) => {
          if (!controller.signal.aborted) {
            setAutocompleteResults(data);
            setIsAutocompleteLoading(false);
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            setIsAutocompleteLoading(false);
          }
        });
    }, debounceMs);
  }, []);

  const clearAutocomplete = useCallback(() => {
    setAutocompleteResults(null);
    acAbortRef.current?.abort();
    if (acTimerRef.current) clearTimeout(acTimerRef.current);
  }, []);

  const doInstantSearch = useCallback((query: string) => {
    if (instantTimerRef.current) clearTimeout(instantTimerRef.current);

    if (!query || query.length < 2) {
      setInstantResults(null);
      return;
    }

    instantPageRef.current = 1;
    instantQueryRef.current = query;
    setIsInstantSearchLoading(true);

    instantTimerRef.current = setTimeout(() => {
      instantAbortRef.current?.abort();
      const controller = new AbortController();
      instantAbortRef.current = controller;

      apiSearch({ q: query, per_page: 20, page: 1, semantic: true, dedup_families: false }, controller.signal)
        .then((data) => {
          if (!controller.signal.aborted) {
            setInstantResults(data);
            setIsInstantSearchLoading(false);
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            setIsInstantSearchLoading(false);
          }
        });
    }, 400);
  }, []);

  const loadMoreInstant = useCallback(() => {
    const query = instantQueryRef.current;
    if (!query || isInstantLoadingMore) return;

    // Don't load if we already have all results
    const current = instantResults?.results?.length ?? 0;
    const total = instantResults?.total ?? 0;
    if (current >= total) return;

    const nextPage = instantPageRef.current + 1;
    setIsInstantLoadingMore(true);

    instantAbortRef.current?.abort();
    const controller = new AbortController();
    instantAbortRef.current = controller;

    apiSearch({ q: query, per_page: 20, page: nextPage, semantic: true, dedup_families: false }, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          instantPageRef.current = nextPage;
          setInstantResults((prev) => {
            if (!prev) return data;
            return {
              ...prev,
              results: [...prev.results, ...data.results],
            };
          });
          setIsInstantLoadingMore(false);
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setIsInstantLoadingMore(false);
        }
      });
  }, [isInstantLoadingMore, instantResults]);

  const clearInstantResults = useCallback(() => {
    setInstantResults(null);
    setIsInstantSearchLoading(false);
    setIsInstantLoadingMore(false);
    instantPageRef.current = 1;
    instantQueryRef.current = '';
    instantAbortRef.current?.abort();
    if (instantTimerRef.current) clearTimeout(instantTimerRef.current);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      searchAbortRef.current?.abort();
      acAbortRef.current?.abort();
      instantAbortRef.current?.abort();
      if (acTimerRef.current) clearTimeout(acTimerRef.current);
      if (instantTimerRef.current) clearTimeout(instantTimerRef.current);
      if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
    };
  }, []);

  return {
    results,
    autocompleteResults,
    instantResults,
    isLoading,
    isAutocompleteLoading,
    isInstantSearchLoading,
    isInstantLoadingMore,
    error,
    doSearch,
    doAutocomplete,
    doInstantSearch,
    loadMoreInstant,
    clearAutocomplete,
    clearInstantResults,
  };
}

/** Push GA4 view_item_list event for search results. */
function pushGA4ViewItemList(query: string, data: SearchResponse) {
  window.dataLayer?.push({
    event: 'view_item_list',
    ecommerce: {
      item_list_name: query,
      items: data.results.slice(0, 20).map((p, i) => ({
        item_name: p.name,
        item_id: p.part_number,
        price: p.price,
        item_category: p.category,
        currency: 'USD',
        index: i,
      })),
    },
  });
}
